/**
 * MODULE: _shared/frontend-shell/src/features/crm/context/CrmActionsContext.tsx
 * PURPOSE: CRM action map from `createCrmActions(useCrm())` — consumed via `useCrmActions` under `CrmShell`
 * DEPENDENCIES: react, react-router-dom, ./CrmContext, ../types/logCallPayload.types, ../utils/crmActions
 * PUBLIC_API: CrmActionsProvider, CrmActionsShellProvider, useCrmActions, LogCallPayload, LogCallOutcome
 * SIDE_EFFECTS: Rebuilds action map when CRM context value identity changes
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 66/200
 * RECENT: 2026-05-11 Task N — **`CrmActionsShellProvider`** wires **`ToastContext`** (when mounted under **`ToastContainer`**) into **`createCrmActions`** for export feedback. 2026-05-07 [2.2.848] Re-export **`LogCallPayload`** for **`logCall`** enriched persist (see **`createCrmActions`** **`logCall`**) — root **`CHANGELOG` [2.2.848]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.748] **`CrmActionsShellProvider`** router wiring for URL **`applyFilters`** or **`resetFilters`**; noop **`CrmActionsProvider`** for Vitest — CHANGELOG [2.2.748]
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContext } from '../../../design-system/components/Feedback/toastContext';
import {
  createCrmActions,
  type CrmActionsMap,
  type CrmActionsRouter,
  type CrmExportToast,
} from '../utils/crmActions';
import { useCrm, useCrmApiBridge, useCrmRollback } from './CrmContext';

const CrmActionsContext = createContext<CrmActionsMap | null>(null);

const noopNavigate: CrmActionsRouter['navigate'] = () => {};

const noopRouter: CrmActionsRouter = {
  navigate: noopNavigate,
  pathname: '/',
};

function CrmActionsProviderImpl({
  children,
  router,
  toast,
}: {
  children: ReactNode;
  router: CrmActionsRouter;
  toast?: CrmExportToast;
}) {
  const ctx = useCrm();
  const api = useCrmApiBridge();
  const rollback = useCrmRollback();
  const actions = useMemo(
    () => createCrmActions(ctx, api, rollback, router, toast),
    [ctx, api, rollback, router, toast]
  );
  return <CrmActionsContext.Provider value={actions}>{children}</CrmActionsContext.Provider>;
}

/**
 * CRM actions without React Router — `applyFilters` and `resetFilters` no-op URL sync (Vitest and legacy harnesses).
 */
export function CrmActionsProvider({ children }: { children: ReactNode }) {
  return <CrmActionsProviderImpl router={noopRouter}>{children}</CrmActionsProviderImpl>;
}

/**
 * CRM actions wired to the current route — use under `BrowserRouter` / `MemoryRouter` (CrmShell, flat CRM routes).
 */
export function CrmActionsShellProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toastCtx = useContext(ToastContext);
  const router = useMemo(() => ({ navigate, pathname }), [navigate, pathname]);
  const toast = useMemo<CrmExportToast | undefined>(() => {
    if (!toastCtx?.showToast) return undefined;
    const { showToast } = toastCtx;
    return {
      success: (message) => showToast({ message, variant: 'success' }),
      error: (message) => showToast({ message, variant: 'error' }),
    };
  }, [toastCtx]);
  return (
    <CrmActionsProviderImpl router={router} toast={toast}>
      {children}
    </CrmActionsProviderImpl>
  );
}

export function useCrmActions(): CrmActionsMap {
  const value = useContext(CrmActionsContext);
  if (!value) {
    throw new Error('useCrmActions must be used within CrmActionsProvider (inside CrmShell)');
  }
  return value;
}

export type { LogCallOutcome, LogCallPayload } from '../types/logCallPayload.types';
